import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  computed,
  inject,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import type { Observable } from 'rxjs';
import { EducationLevel } from '../../../api/models/education-level';
import { EducationProfile } from '../../../api/models/education-profile';
import { SchoolType } from '../../../api/models/school-type';

type LoadFn = (ctx: { injector: Injector }) => Observable<EducationLevel[]>;

interface SelectedEducationLevel {
  id: string;
  // The BE EducationLevel DTO has `name` as required. Sending only `id`
  // makes Create/Update Job Posting fail with `EducationLevels[0].Name
  // is required` — so we round-trip the level's display fields too.
  name: string;
  level?: number | null;
  schoolType?: SchoolType;
  regions?: string[];
  // EducationProfile is a `{ id, name, code }` object in the BE DTO; an
  // id-only `string[]` would 400 with "The JSON value could not be
  // converted to … EducationProfile" (Sentry MEETTHEYOUNGSTERS-5V).
  educationProfiles?: EducationProfile[];
  phases?: string[];
}

const BANDS = [SchoolType.Middelbaar, SchoolType.Vervolgonderwijs] as const;

const BAND_STYLES: Record<string, { header: string; body: string }> = {
  [SchoolType.Middelbaar]: {
    header: 'linear-gradient(120deg, #1e5ea0 0%, #357bbf 55%, #4a8ecb 100%)',
    body: 'linear-gradient(180deg, rgba(53,123,191,0.055) 0%, rgba(53,123,191,0.02) 100%)',
  },
  [SchoolType.Vervolgonderwijs]: {
    header: 'linear-gradient(120deg, #1290bf 0%, #29b9e3 55%, #3ecdf5 100%)',
    body: 'linear-gradient(180deg, rgba(41,185,227,0.055) 0%, rgba(41,185,227,0.02) 100%)',
  },
};

@Component({
  selector: 'app-formly-education-level-tree',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatCheckboxModule, TranslateModule, FormlyModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-5" [attr.data-field-id]="field.id">
      @if (props.label) {
        <span [class]="labelClass()">
          {{ props.label | translate }}
          @if (props.required) { <span class="text-mty-danger ml-0.5">*</span> }
        </span>
      }

      @for (band of bands; track band) {
        @if (getRegionsForBand(band).length > 0) {
          <div class="rounded-[20px] overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04),0_3px_12px_rgba(0,0,0,0.06)]">

            <!-- Band header -->
            <div class="flex items-center justify-between px-5 py-3"
                 [style.background]="bandStyles[band].header">
              <span class="font-alternate text-[13.5px] font-semibold text-white tracking-[0.01em]">
                {{ band | translate }}
              </span>
              @if (selectedCountForBand(band) > 0) {
                <span class="inline-flex items-center justify-center min-w-[20px] h-[20px] px-[7px] rounded-full bg-white/[0.22] text-white/95 font-alternate text-[11px] font-semibold">
                  {{ selectedCountForBand(band) }}
                </span>
              }
            </div>

            <!-- Band body -->
            <div [style.background]="bandStyles[band].body"
                 [class]="isSingleRegion(band) ? 'flex flex-col' : 'grid'"
                 [style.grid-template-columns]="isSingleRegion(band) ? null : 'repeat(' + getRegionsForBand(band).length + ', minmax(0, 1fr))'">

              <!-- Region column headers (only when multiple regions) -->
              @if (!isSingleRegion(band)) {
                @for (region of getRegionsForBand(band); track region; let last = $last) {
                  <div [class]="'pt-3 pb-2 text-center font-alternate text-[10px] font-semibold tracking-[0.14em] uppercase text-black/35'
                                + (last ? '' : ' border-r border-black/[0.07]')">
                    {{ region }}
                  </div>
                }
                <div class="mx-3 mb-2 h-px bg-black/[0.07]" style="grid-column: 1 / -1"></div>
              } @else {
                <div class="pt-3"></div>
              }

              <!-- Level rows -->
              @for (levelNum of uniqueLevelsForBand(band); track levelNum) {
                @for (region of getRegionsForBand(band); track region; let last = $last) {
                  <div [class]="'px-3 pt-[3px]' + (last ? '' : ' border-r border-black/[0.07]')">
                    @if (getLevelInRegion(band, region, levelNum); as level) {

                      <!-- Pill button -->
                      <button
                        type="button"
                        class="block w-full px-[11px] py-[6px] mb-[5px] rounded-full text-[12.5px] font-medium leading-[1.4] text-center cursor-pointer transition-all duration-[130ms]"
                        [class]="isLevelChecked(level.id!)
                          ? 'bg-[#357bbf] text-white border-[1.5px] border-transparent shadow-[0_1px_5px_rgba(53,123,191,0.32)] hover:bg-[#2b6db5]'
                          : 'bg-white text-black/60 border-[1.5px] border-black/[0.09] hover:border-[rgba(53,123,191,0.42)] hover:bg-[rgba(53,123,191,0.045)] hover:text-black/80 hover:-translate-y-px'"
                        [title]="level.name"
                        (click)="toggleLevel(level, !isLevelChecked(level.id!))">
                        {{ level.name }}
                      </button>

                      <!-- Inline details: profiles + phases -->
                      @if (isLevelChecked(level.id!)) {
                        <div class="mb-[8px] px-1 pb-2 rounded-xl bg-black/[0.025] border border-black/[0.07]">

                          @if (level.educationProfiles?.length) {
                            <fieldset class="border-0 p-0 m-0 px-2 pt-2">
                              <legend class="inline-block font-alternate text-[10px] font-semibold tracking-wider uppercase text-black/35 mb-1">
                                {{ 'Profiles' | translate }}
                              </legend>
                              <div class="flex flex-col gap-0.5">
                                @for (p of level.educationProfiles ?? []; track p.id) {
                                  <mat-checkbox color="primary"
                                    [checked]="isProfileChecked(level.id!, p.id!)"
                                    (change)="toggleProfile(level.id!, p, $event.checked)">
                                    <span class="text-[12px]">{{ p.name }}</span>
                                  </mat-checkbox>
                                }
                              </div>
                            </fieldset>
                          }

                          @if (level.phases?.length) {
                            <fieldset class="border-0 p-0 m-0 px-2 pt-2">
                              <legend class="inline-block font-alternate text-[10px] font-semibold tracking-wider uppercase text-black/35 mb-1">
                                {{ 'Phases' | translate }}
                              </legend>
                              <div class="flex flex-col gap-0.5">
                                @for (ph of level.phases ?? []; track ph) {
                                  <mat-checkbox color="primary"
                                    [checked]="isPhaseChecked(level.id!, ph)"
                                    (change)="togglePhase(level.id!, ph, $event.checked)">
                                    <span class="text-[12px]">{{ ph }}</span>
                                  </mat-checkbox>
                                }
                              </div>
                            </fieldset>
                          }

                        </div>
                      }

                    } @else {
                      <div class="block w-full px-[11px] py-[6px] mb-[5px] rounded-full text-center text-[12.5px] text-black/[0.2] border-[1.5px] border-dashed border-black/[0.07] select-none">—</div>
                    }
                  </div>
                }
              }

              <!-- Bottom padding -->
              @for (region of getRegionsForBand(band); track region) {
                <div class="pb-3"></div>
              }

            </div>
          </div>
        }
      }

      @if (isInvalid()) {
        <span class="font-base text-[12px] text-mty-danger">
          {{ 'This field is required' | translate }}
        </span>
      }
    </div>
  `,
})
export class EducationLevelTreeFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly bands = BANDS;
  readonly bandStyles = BAND_STYLES;

  private readonly loadedLevels = signal<EducationLevel[] | null>(null);

  ngOnInit(): void {
    const loadFn = this.props['loadOptions'] as LoadFn | undefined;
    if (typeof loadFn !== 'function') return;
    runInInjectionContext(this.injector, () => {
      loadFn({ injector: this.injector })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          this.loadedLevels.set(result ?? []);
          this.cdr.markForCheck();
        });
    });
  }

  readonly groupedLevels = computed(() => {
    const loaded = this.loadedLevels();
    const all = loaded !== null ? loaded : ((this.props['educationLevels'] as EducationLevel[]) ?? []);
    const exclude = new Set<string>((this.props['excludedRegions'] as string[] | undefined) ?? ['EU']);

    const groups: Record<string, Record<string, EducationLevel[]>> = {
      [SchoolType.Middelbaar]: {},
      [SchoolType.Vervolgonderwijs]: {},
    };

    for (const lvl of all) {
      const band = lvl.schoolType ?? SchoolType.Middelbaar;
      for (const region of lvl.regions ?? []) {
        if (exclude.has(region)) continue;
        groups[band][region] ??= [];
        groups[band][region].push(lvl);
      }
    }

    for (const band of Object.keys(groups)) {
      for (const region of Object.keys(groups[band])) {
        groups[band][region].sort((a, b) => (a.level ?? 0) - (b.level ?? 0));
      }
    }

    return groups;
  });

  getRegionsForBand(band: string): string[] {
    return Object.keys(this.groupedLevels()[band] ?? {});
  }

  uniqueLevelsForBand(band: string): number[] {
    const grouped = this.groupedLevels()[band] ?? {};
    const nums = new Set<number>();
    for (const items of Object.values(grouped)) {
      for (const item of items) {
        if (item.level != null) nums.add(item.level);
      }
    }
    return [...nums].sort((a, b) => a - b);
  }

  getLevelInRegion(band: string, region: string, levelNum: number): EducationLevel | undefined {
    return (this.groupedLevels()[band]?.[region] ?? []).find((item) => item.level === levelNum);
  }

  selectedCountForBand(band: string): number {
    const sel = new Set(this.value().map((l) => l.id));
    const grouped = this.groupedLevels()[band] ?? {};
    const inBand = new Set<string>();
    for (const levels of Object.values(grouped)) {
      for (const level of levels) {
        if (level.id) inBand.add(level.id);
      }
    }
    return [...inBand].filter((id) => sel.has(id)).length;
  }

  isSingleRegion(band: string): boolean {
    return this.getRegionsForBand(band).length === 1;
  }

  private value(): SelectedEducationLevel[] {
    const v = this.formControl.value;
    return Array.isArray(v) ? (v as SelectedEducationLevel[]) : [];
  }

  isLevelChecked(id: string): boolean {
    return this.value().some((l) => l.id === id);
  }

  isProfileChecked(lvlId: string, pid: string): boolean {
    return (
      this.value()
        .find((l) => l.id === lvlId)
        ?.educationProfiles?.some((p) => p.id === pid) ?? false
    );
  }

  isPhaseChecked(lvlId: string, ph: string): boolean {
    return this.value().find((l) => l.id === lvlId)?.phases?.includes(ph) ?? false;
  }

  toggleLevel(level: EducationLevel, on: boolean): void {
    const id = level.id!;
    let next = this.value().slice();
    if (on) {
      next.push({
        id,
        name: level.name,
        level: level.level,
        schoolType: level.schoolType,
        regions: level.regions ?? [],
        educationProfiles: [],
        phases: [],
      });
    } else {
      next = next.filter((l) => l.id !== id);
    }
    this.formControl.setValue(next);
    this.formControl.markAsDirty();
    this.cdr.markForCheck();
  }

  toggleProfile(lvlId: string, profile: EducationProfile, on: boolean): void {
    const next = this.value().map((l) =>
      l.id === lvlId
        ? {
            ...l,
            educationProfiles: on
              ? [...(l.educationProfiles ?? []), profile]
              : (l.educationProfiles ?? []).filter((p) => p.id !== profile.id),
          }
        : l,
    );
    this.formControl.setValue(next);
    this.formControl.markAsDirty();
    this.cdr.markForCheck();
  }

  togglePhase(lvlId: string, ph: string, on: boolean): void {
    const next = this.value().map((l) =>
      l.id === lvlId
        ? {
            ...l,
            phases: on
              ? [...(l.phases ?? []), ph]
              : (l.phases ?? []).filter((x) => x !== ph),
          }
        : l,
    );
    this.formControl.setValue(next);
    this.formControl.markAsDirty();
    this.cdr.markForCheck();
  }

  isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  labelClass(): string {
    const base = 'font-alternate text-[12px] font-semibold tracking-wider uppercase';
    return this.isInvalid() ? `${base} text-mty-danger` : `${base} text-black/55`;
  }
}
